import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, Container, Form, Modal, Spinner, Table } from "react-bootstrap";
import API from "../services/api";
import DepartmentListPage, { fmtDate, orDash } from "./departments/DepartmentShared";
import HrEmployeesManage from "./hr/HrEmployeesManage";
import SalesMetaLeadsPage from "./SalesMetaLeads";

const statusBadge = (value) => (
  <span
    style={{
      padding: "2px 8px",
      borderRadius: "999px",
      fontSize: "11px",
      fontWeight: 700,
      background: "#e2e8f0",
      color: "#0f172a",
    }}
  >
    {value || "—"}
  </span>
);

export const HrEmployees = () => <HrEmployeesManage />;

export const HrLeaveRequests = () => (
  <DepartmentListPage
    title="Leave Requests"
    subtitle="Pending and approved leave requests."
    endpoint="/employee/hr/leave-requests"
    columns={[
      {
        key: "employeeName",
        label: "Employee",
        render: (r) => r.employeeName || r.employee?.fullName || "—",
      },
      { key: "leaveType", label: "Type" },
      { key: "fromDate", label: "From", render: (r) => fmtDate(r.fromDate) },
      { key: "toDate", label: "To", render: (r) => fmtDate(r.toDate) },
      { key: "reason", label: "Reason" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "employeeName", label: "Employee Name", required: true },
      { key: "leaveType", label: "Leave Type" },
      { key: "fromDate", label: "From Date", type: "date" },
      { key: "toDate", label: "To Date", type: "date" },
      { key: "reason", label: "Reason" },
    ]}
    createPerms={["leave:view"]}
    approvePerms={["leave:approve"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const HrAttendanceApprovals = () => (
  <DepartmentListPage
    title="Attendance Approvals"
    subtitle="Manual attendance entries requiring verification."
    endpoint="/employee/hr/attendance-approvals"
    columns={[
      { key: "employee", label: "Employee", render: (r) => r.employee?.fullName || "—" },
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "workMinutes", label: "Work (min)", render: (r) => orDash(r.workMinutes) },
      { key: "address", label: "Address", render: (r) => orDash(r.address) },
      { key: "approvalStatus", label: "Status", render: (r) => statusBadge(r.approvalStatus) },
    ]}
    approvePerms={["attendance:approve"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const HrPerformanceReviews = () => (
  <DepartmentListPage
    title="Performance Reviews"
    subtitle="Review cycles, ratings, and notes."
    endpoint="/employee/hr/performance-reviews"
    columns={[
      {
        key: "employeeName",
        label: "Employee",
        render: (r) => r.employeeName || r.employee?.fullName || "—",
      },
      { key: "period", label: "Period" },
      { key: "rating", label: "Rating" },
      { key: "reviewer", label: "Reviewer" },
      { key: "summary", label: "Summary" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "employeeName", label: "Employee Name", required: true },
      { key: "period", label: "Review Period" },
      { key: "rating", label: "Rating" },
      { key: "reviewer", label: "Reviewer" },
      { key: "summary", label: "Summary" },
    ]}
    createPerms={["performance:view"]}
    approvePerms={["performance:view"]}
    statusActions={[
      { label: "Mark Completed", value: "completed", variant: "success" },
      { label: "Reopen", value: "pending", variant: "outline-secondary" },
    ]}
  />
);

export const HrReports = () => (
  <DepartmentListPage
    title="HR Reports"
    subtitle="Monthly HR highlights and compliance snapshots."
    endpoint="/employee/hr/reports"
    columns={[
      { key: "title", label: "Report" },
      { key: "period", label: "Period" },
      { key: "owner", label: "Owner" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "title", label: "Report Title", required: true },
      { key: "period", label: "Period" },
      { key: "owner", label: "Owner" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["reports:hr:view"]}
    approvePerms={["reports:hr:view"]}
    statusActions={[
      { label: "Publish", value: "published", variant: "success" },
      { label: "Draft", value: "draft", variant: "outline-secondary" },
    ]}
  />
);

export const HrOnboarding = () => (
  <DepartmentListPage
    title="Onboarding Tracker"
    subtitle="Track new joiners, owners, and onboarding checklists."
    endpoint="/employee/hr/onboarding"
    columns={[
      { key: "candidateName", label: "Candidate" },
      { key: "role", label: "Role" },
      { key: "joiningDate", label: "Joining", render: (r) => fmtDate(r.joiningDate) },
      { key: "owner", label: "Owner" },
      { key: "checklist", label: "Checklist" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "candidateName", label: "Candidate Name", required: true },
      { key: "role", label: "Role" },
      { key: "joiningDate", label: "Joining Date", type: "date" },
      { key: "owner", label: "Owner" },
      { key: "checklist", label: "Checklist" },
    ]}
    createPerms={["hr:onboarding:manage"]}
    approvePerms={["hr:onboarding:manage"]}
    statusActions={[
      { label: "Start", value: "in-progress", variant: "success" },
      { label: "Complete", value: "completed", variant: "outline-primary" },
    ]}
  />
);

export const HrOffboarding = () => (
  <DepartmentListPage
    title="Offboarding"
    subtitle="Exit process, handover, and clearance tracking."
    endpoint="/employee/hr/offboarding"
    columns={[
      { key: "employeeName", label: "Employee" },
      { key: "lastWorkingDay", label: "Last Day", render: (r) => fmtDate(r.lastWorkingDay) },
      { key: "reason", label: "Reason" },
      { key: "owner", label: "Owner" },
      { key: "handoverStatus", label: "Handover" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "employeeName", label: "Employee Name", required: true },
      { key: "lastWorkingDay", label: "Last Working Day", type: "date" },
      { key: "reason", label: "Reason" },
      { key: "owner", label: "Owner" },
      { key: "handoverStatus", label: "Handover Status" },
    ]}
    createPerms={["hr:offboarding:manage"]}
    approvePerms={["hr:offboarding:manage"]}
    statusActions={[
      { label: "In Progress", value: "in-progress", variant: "success" },
      { label: "Complete", value: "completed", variant: "outline-primary" },
    ]}
  />
);

export const HrRecruitment = () => (
  <DepartmentListPage
    title="Recruitment Pipeline"
    subtitle="Candidate stages from sourcing to offer."
    endpoint="/employee/hr/recruitment"
    columns={[
      { key: "candidateName", label: "Candidate" },
      { key: "role", label: "Role" },
      { key: "stage", label: "Stage" },
      { key: "source", label: "Source" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "candidateName", label: "Candidate Name", required: true },
      { key: "role", label: "Role" },
      { key: "stage", label: "Stage" },
      { key: "source", label: "Source" },
      { key: "owner", label: "Owner" },
    ]}
    createPerms={["hr:recruitment:manage"]}
    approvePerms={["hr:recruitment:manage"]}
    statusActions={[
      { label: "Move to Interview", value: "interview", variant: "success" },
      { label: "Offer", value: "offer", variant: "outline-primary" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const HrPolicies = () => (
  <DepartmentListPage
    title="HR Policies"
    subtitle="Policy versions, owners, and effective dates."
    endpoint="/employee/hr/policies"
    columns={[
      { key: "policyTitle", label: "Policy" },
      { key: "version", label: "Version" },
      { key: "owner", label: "Owner" },
      { key: "effectiveDate", label: "Effective", render: (r) => fmtDate(r.effectiveDate) },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "policyTitle", label: "Policy Title", required: true },
      { key: "version", label: "Version" },
      { key: "owner", label: "Owner" },
      { key: "effectiveDate", label: "Effective Date", type: "date" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["hr:policies:manage"]}
    approvePerms={["hr:policies:manage"]}
    statusActions={[
      { label: "Publish", value: "published", variant: "success" },
      { label: "Draft", value: "draft", variant: "outline-secondary" },
    ]}
  />
);

export const HrTraining = () => (
  <DepartmentListPage
    title="Training Programs"
    subtitle="Planned trainings, trainers, and participants."
    endpoint="/employee/hr/training"
    columns={[
      { key: "programName", label: "Program" },
      { key: "trainer", label: "Trainer" },
      { key: "startDate", label: "Start", render: (r) => fmtDate(r.startDate) },
      { key: "endDate", label: "End", render: (r) => fmtDate(r.endDate) },
      { key: "participants", label: "Participants" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "programName", label: "Program Name", required: true },
      { key: "trainer", label: "Trainer" },
      { key: "startDate", label: "Start Date", type: "date" },
      { key: "endDate", label: "End Date", type: "date" },
      { key: "participants", label: "Participants" },
    ]}
    createPerms={["hr:training:manage"]}
    approvePerms={["hr:training:manage"]}
    statusActions={[
      { label: "Start", value: "ongoing", variant: "success" },
      { label: "Complete", value: "completed", variant: "outline-primary" },
    ]}
  />
);

export const HrGrievance = () => (
  <DepartmentListPage
    title="Grievance Cases"
    subtitle="Employee grievances and resolution tracking."
    endpoint="/employee/hr/grievance"
    columns={[
      { key: "caseTitle", label: "Case" },
      { key: "raisedBy", label: "Raised By" },
      { key: "severity", label: "Severity" },
      { key: "assignedTo", label: "Assigned To" },
      { key: "summary", label: "Summary" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "caseTitle", label: "Case Title", required: true },
      { key: "raisedBy", label: "Raised By" },
      { key: "severity", label: "Severity" },
      { key: "assignedTo", label: "Assigned To" },
      { key: "summary", label: "Summary" },
    ]}
    createPerms={["hr:grievance:manage"]}
    approvePerms={["hr:grievance:manage"]}
    statusActions={[
      { label: "Review", value: "in-review", variant: "success" },
      { label: "Resolve", value: "resolved", variant: "outline-primary" },
      { label: "Close", value: "closed", variant: "outline-secondary" },
    ]}
  />
);

export const HrVacancies = () => (
  <DepartmentListPage
    title="Job Vacancies"
    subtitle="Open roles and hiring status."
    endpoint="/employee/hr/vacancies"
    columns={[
      { key: "title", label: "Title" },
      { key: "department", label: "Department" },
      { key: "openings", label: "Openings" },
      { key: "location", label: "Location" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "title", label: "Job Title", required: true },
      { key: "department", label: "Department" },
      { key: "openings", label: "Openings" },
      { key: "location", label: "Location" },
    ]}
    createPerms={["hr:vacancy:manage"]}
    approvePerms={["hr:vacancy:manage"]}
    statusActions={[
      { label: "Open", value: "open", variant: "success" },
      { label: "On Hold", value: "on-hold", variant: "outline-warning" },
      { label: "Close", value: "closed", variant: "outline-danger" },
    ]}
  />
);

export const HrInterviews = () => (
  <DepartmentListPage
    title="Interview Schedule"
    subtitle="Schedule, panel, and interview status."
    endpoint="/employee/hr/interviews"
    columns={[
      { key: "candidateName", label: "Candidate" },
      { key: "role", label: "Role" },
      { key: "interviewDate", label: "Date", render: (r) => fmtDate(r.interviewDate) },
      { key: "panel", label: "Panel" },
      { key: "mode", label: "Mode" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "candidateName", label: "Candidate Name", required: true },
      { key: "role", label: "Role" },
      { key: "interviewDate", label: "Interview Date", type: "date" },
      { key: "panel", label: "Panel" },
      { key: "mode", label: "Mode" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["hr:interview:manage"]}
    approvePerms={["hr:interview:manage"]}
    statusActions={[
      { label: "Complete", value: "completed", variant: "success" },
      { label: "Cancel", value: "cancelled", variant: "outline-danger" },
    ]}
  />
);

export const HrOfferLetters = () => (
  <DepartmentListPage
    title="Offer Letters"
    subtitle="Draft, sent, accepted offers."
    endpoint="/employee/hr/offer-letters"
    columns={[
      { key: "candidateName", label: "Candidate" },
      { key: "role", label: "Role" },
      { key: "ctc", label: "CTC" },
      { key: "joiningDate", label: "Joining", render: (r) => fmtDate(r.joiningDate) },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "candidateName", label: "Candidate Name", required: true },
      { key: "role", label: "Role" },
      { key: "ctc", label: "CTC" },
      { key: "joiningDate", label: "Joining Date", type: "date" },
      { key: "content", label: "Offer Content" },
    ]}
    createPerms={["hr:offer-letter:manage"]}
    approvePerms={["hr:offer-letter:manage"]}
    statusActions={[
      { label: "Send", value: "sent", variant: "success" },
      { label: "Accepted", value: "accepted", variant: "outline-primary" },
      { label: "Rejected", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const HrHolidays = () => (
  <DepartmentListPage
    title="Holiday List"
    subtitle="Official holidays and company off days."
    endpoint="/employee/hr/holidays"
    columns={[
      { key: "title", label: "Holiday" },
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "type", label: "Type" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "title", label: "Holiday Title", required: true },
      { key: "date", label: "Date", type: "date" },
      { key: "type", label: "Type" },
    ]}
    createPerms={["hr:holiday:manage"]}
    approvePerms={["hr:holiday:manage"]}
    statusActions={[
      { label: "Activate", value: "active", variant: "success" },
      { label: "Deactivate", value: "inactive", variant: "outline-danger" },
    ]}
  />
);

export const HrAttendanceRules = () => (
  <DepartmentListPage
    title="Attendance Rules"
    subtitle="Late grace and half-day rules."
    endpoint="/employee/hr/attendance-rules"
    columns={[
      { key: "ruleName", label: "Rule" },
      { key: "lateGraceMinutes", label: "Late Grace (min)" },
      { key: "halfDayAfterMinutes", label: "Half-day After (min)" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "ruleName", label: "Rule Name", required: true },
      { key: "lateGraceMinutes", label: "Late Grace Minutes" },
      { key: "halfDayAfterMinutes", label: "Half-day After Minutes" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["hr:attendance-rule:manage"]}
    approvePerms={["hr:attendance-rule:manage"]}
    statusActions={[
      { label: "Activate", value: "active", variant: "success" },
      { label: "Deactivate", value: "inactive", variant: "outline-danger" },
    ]}
  />
);

export const HrSalaryStructures = () => (
  <DepartmentListPage
    title="Salary Structures"
    subtitle="Basic, HRA, allowances and deductions."
    endpoint="/employee/hr/salary-structures"
    columns={[
      {
        key: "employeeName",
        label: "Employee",
        render: (r) => r.employeeName || r.employee?.fullName || "—",
      },
      { key: "basic", label: "Basic" },
      { key: "hra", label: "HRA" },
      { key: "allowances", label: "Allowances" },
      { key: "deductions", label: "Deductions" },
      { key: "netPay", label: "Net Pay" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "employeeName", label: "Employee Name", required: true },
      { key: "basic", label: "Basic" },
      { key: "hra", label: "HRA" },
      { key: "allowances", label: "Allowances" },
      { key: "deductions", label: "Deductions" },
      { key: "netPay", label: "Net Pay" },
    ]}
    createPerms={["hr:salary-structure:manage"]}
    approvePerms={["hr:salary-structure:manage"]}
    statusActions={[
      { label: "Activate", value: "active", variant: "success" },
      { label: "Deactivate", value: "inactive", variant: "outline-danger" },
    ]}
  />
);

export const HrPayslips = () => (
  <DepartmentListPage
    title="Payslips"
    subtitle="Generated payslips and distribution."
    endpoint="/employee/hr/payslips"
    columns={[
      {
        key: "employeeName",
        label: "Employee",
        render: (r) => r.employeeName || r.employee?.fullName || "—",
      },
      { key: "month", label: "Month" },
      { key: "grossPay", label: "Gross" },
      { key: "deductions", label: "Deductions" },
      { key: "netPay", label: "Net" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "employeeName", label: "Employee Name", required: true },
      { key: "month", label: "Month" },
      { key: "grossPay", label: "Gross Pay" },
      { key: "deductions", label: "Deductions" },
      { key: "netPay", label: "Net Pay" },
    ]}
    createPerms={["hr:payslip:manage"]}
    approvePerms={["hr:payslip:manage"]}
    statusActions={[
      { label: "Send", value: "sent", variant: "success" },
      { label: "Generated", value: "generated", variant: "outline-secondary" },
    ]}
  />
);

export const HrResignations = () => (
  <DepartmentListPage
    title="Resignation Requests"
    subtitle="Notice period tracking and approvals."
    endpoint="/employee/hr/resignations"
    columns={[
      {
        key: "employeeName",
        label: "Employee",
        render: (r) => r.employeeName || r.employee?.fullName || "—",
      },
      { key: "noticePeriodDays", label: "Notice (days)" },
      { key: "lastWorkingDay", label: "Last Day", render: (r) => fmtDate(r.lastWorkingDay) },
      { key: "reason", label: "Reason" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "employeeName", label: "Employee Name", required: true },
      { key: "noticePeriodDays", label: "Notice Period (days)" },
      { key: "lastWorkingDay", label: "Last Working Day", type: "date" },
      { key: "reason", label: "Reason" },
    ]}
    createPerms={["hr:resignation:manage"]}
    approvePerms={["hr:resignation:manage"]}
    statusActions={[
      { label: "Accept", value: "accepted", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
      { label: "In Progress", value: "in-progress", variant: "outline-secondary" },
      { label: "Close", value: "closed", variant: "outline-primary" },
    ]}
  />
);

export const HrExitInterviews = () => (
  <DepartmentListPage
    title="Exit Interviews"
    subtitle="Interview schedule and feedback."
    endpoint="/employee/hr/exit-interviews"
    columns={[
      {
        key: "employeeName",
        label: "Employee",
        render: (r) => r.employeeName || r.employee?.fullName || "—",
      },
      { key: "exitDate", label: "Exit Date", render: (r) => fmtDate(r.exitDate) },
      { key: "interviewer", label: "Interviewer" },
      { key: "feedback", label: "Feedback" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "employeeName", label: "Employee Name", required: true },
      { key: "exitDate", label: "Exit Date", type: "date" },
      { key: "interviewer", label: "Interviewer" },
      { key: "feedback", label: "Feedback" },
    ]}
    createPerms={["hr:exit-interview:manage"]}
    approvePerms={["hr:exit-interview:manage"]}
    statusActions={[
      { label: "Complete", value: "completed", variant: "success" },
      { label: "Schedule", value: "scheduled", variant: "outline-secondary" },
    ]}
  />
);

export const HrLetters = () => (
  <DepartmentListPage
    title="HR Letters"
    subtitle="Offer, appointment, relieving, experience letters."
    endpoint="/employee/hr/letters"
    columns={[
      {
        key: "employeeName",
        label: "Employee",
        render: (r) => r.employeeName || r.employee?.fullName || "—",
      },
      { key: "type", label: "Type" },
      { key: "subject", label: "Subject" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "employeeName", label: "Employee Name", required: true },
      { key: "type", label: "Type" },
      { key: "subject", label: "Subject" },
      { key: "content", label: "Content" },
    ]}
    createPerms={["hr:letters:manage"]}
    approvePerms={["hr:letters:manage"]}
    statusActions={[
      { label: "Issue", value: "issued", variant: "success" },
      { label: "Draft", value: "draft", variant: "outline-secondary" },
    ]}
  />
);

export const HrDocuments = () => (
  <DepartmentListPage
    title="Employee Documents"
    subtitle="Aadhar, PAN, address proof, resume."
    endpoint="/employee/hr/documents"
    columns={[
      { key: "fullName", label: "Employee" },
      { key: "mobile", label: "Mobile" },
      { key: "employeeDocuments", label: "Aadhar", render: (r) => orDash(r.employeeDocuments?.aadhar) },
      { key: "employeeDocuments", label: "PAN", render: (r) => orDash(r.employeeDocuments?.pan) },
      { key: "employeeDocuments", label: "Address", render: (r) => orDash(r.employeeDocuments?.addressProof) },
      { key: "employeeDocuments", label: "Resume", render: (r) => orDash(r.employeeDocuments?.resume) },
    ]}
  />
);

export const HrPayrollRuns = () => (
  <DepartmentListPage
    title="Payroll Runs"
    subtitle="Monthly salary processing batches."
    endpoint="/employee/hr/payroll-runs"
    columns={[
      { key: "month", label: "Month" },
      { key: "cycle", label: "Cycle" },
      { key: "totalEmployees", label: "Employees" },
      { key: "totalPayout", label: "Total Payout" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "month", label: "Month", required: true },
      { key: "cycle", label: "Cycle" },
      { key: "totalEmployees", label: "Total Employees" },
      { key: "totalPayout", label: "Total Payout" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["hr:payroll:manage"]}
    approvePerms={["hr:payroll:manage"]}
    statusActions={[
      { label: "Process", value: "processed", variant: "success" },
      { label: "Approve", value: "approved", variant: "outline-primary" },
      { label: "Close", value: "closed", variant: "outline-secondary" },
    ]}
  />
);

export const HrDeductionRules = () => (
  <DepartmentListPage
    title="Deduction Rules"
    subtitle="PF, ESI, leave cut, and other rules."
    endpoint="/employee/hr/deduction-rules"
    columns={[
      { key: "name", label: "Rule" },
      { key: "type", label: "Type" },
      { key: "amount", label: "Amount" },
      { key: "percent", label: "Percent" },
      { key: "appliesTo", label: "Applies To" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "name", label: "Rule Name", required: true },
      { key: "type", label: "Type" },
      { key: "amount", label: "Amount" },
      { key: "percent", label: "Percent" },
      { key: "appliesTo", label: "Applies To" },
    ]}
    createPerms={["hr:deduction:manage"]}
    approvePerms={["hr:deduction:manage"]}
    statusActions={[
      { label: "Activate", value: "active", variant: "success" },
      { label: "Deactivate", value: "inactive", variant: "outline-danger" },
    ]}
  />
);

export const HrCandidateProfiles = () => (
  <DepartmentListPage
    title="Candidate Profiles"
    subtitle="Resumes and candidate details."
    endpoint="/employee/hr/candidates"
    columns={[
      { key: "candidateName", label: "Candidate" },
      { key: "role", label: "Role" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "resumeLink", label: "Resume" },
      { key: "source", label: "Source" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "candidateName", label: "Candidate Name", required: true },
      { key: "role", label: "Role" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "resumeLink", label: "Resume Link" },
      { key: "source", label: "Source" },
    ]}
    createPerms={["hr:candidate:manage"]}
    approvePerms={["hr:candidate:manage"]}
    statusActions={[
      { label: "Shortlist", value: "shortlisted", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
      { label: "Review", value: "reviewed", variant: "outline-secondary" },
    ]}
  />
);

export const HrAppraisals = () => (
  <DepartmentListPage
    title="Promotion & Increment"
    subtitle="Appraisal recommendations for increments and promotions."
    endpoint="/employee/hr/appraisals"
    columns={[
      {
        key: "employeeName",
        label: "Employee",
        render: (r) => r.employeeName || r.employee?.fullName || "—",
      },
      { key: "period", label: "Period" },
      { key: "recommendation", label: "Recommendation" },
      { key: "incrementPercent", label: "Increment %" },
      { key: "promotionTo", label: "Promotion To" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "employeeName", label: "Employee Name", required: true },
      { key: "period", label: "Period" },
      { key: "recommendation", label: "Recommendation" },
      { key: "incrementPercent", label: "Increment %" },
      { key: "promotionTo", label: "Promotion To" },
    ]}
    createPerms={["hr:appraisal:manage"]}
    approvePerms={["hr:appraisal:manage"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const SalesLeads = () => (
  <DepartmentListPage
    title="Sales Leads"
    subtitle="Leads pipeline and qualification stages."
    endpoint="/employee/sales/leads"
    columns={[
      { key: "leadName", label: "Lead" },
      { key: "company", label: "Company" },
      { key: "contact", label: "Contact" },
      { key: "stage", label: "Stage" },
      { key: "value", label: "Value" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "leadName", label: "Lead Name", required: true },
      { key: "company", label: "Company" },
      { key: "contact", label: "Contact" },
      { key: "stage", label: "Stage" },
      { key: "value", label: "Estimated Value" },
    ]}
    createPerms={["leads:edit"]}
    approvePerms={["leads:edit"]}
    statusActions={[
      { label: "Qualify", value: "qualified", variant: "success" },
      { label: "Lost", value: "lost", variant: "outline-danger" },
    ]}
  />
);

export const SalesMetaLeads = () => <SalesMetaLeadsPage />;

export const SalesDeals = () => (
  <DepartmentListPage
    title="Sales Deals"
    subtitle="Active deals with close dates and owners."
    endpoint="/employee/sales/deals"
    columns={[
      { key: "dealName", label: "Deal" },
      { key: "client", label: "Client" },
      { key: "amount", label: "Amount" },
      { key: "closeDate", label: "Close Date", render: (r) => fmtDate(r.closeDate) },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "dealName", label: "Deal Name", required: true },
      { key: "client", label: "Client" },
      { key: "amount", label: "Amount" },
      { key: "closeDate", label: "Close Date", type: "date" },
      { key: "owner", label: "Owner" },
    ]}
    createPerms={["deals:edit"]}
    approvePerms={["deals:edit"]}
    statusActions={[
      { label: "Won", value: "won", variant: "success" },
      { label: "Lost", value: "lost", variant: "outline-danger" },
    ]}
  />
);

export const SalesTargets = () => (
  <DepartmentListPage
    title="Sales Targets"
    subtitle="Targets vs. achievements by period."
    endpoint="/employee/sales/targets"
    columns={[
      { key: "period", label: "Period" },
      { key: "owner", label: "Owner" },
      { key: "targetValue", label: "Target" },
      { key: "achievedValue", label: "Achieved" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "period", label: "Period", required: true },
      { key: "owner", label: "Owner" },
      { key: "targetValue", label: "Target Value" },
      { key: "achievedValue", label: "Achieved Value" },
    ]}
    createPerms={["sales:targets:manage"]}
    approvePerms={["sales:targets:manage"]}
    statusActions={[
      { label: "Close", value: "closed", variant: "success" },
      { label: "Reopen", value: "open", variant: "outline-secondary" },
    ]}
  />
);

export const SalesPricingApprovals = () => (
  <DepartmentListPage
    title="Pricing & Discount Approvals"
    subtitle="Approval queue for pricing exceptions."
    endpoint="/employee/sales/pricing-approvals"
    columns={[
      { key: "requestName", label: "Request" },
      { key: "client", label: "Client" },
      { key: "discount", label: "Discount %" },
      { key: "requestedBy", label: "Requested By" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "requestName", label: "Request Name", required: true },
      { key: "client", label: "Client" },
      { key: "discount", label: "Discount %" },
      { key: "requestedBy", label: "Requested By" },
    ]}
    createPerms={["pricing:approve"]}
    approvePerms={["pricing:approve", "discount:approve"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const SalesReports = () => (
  <DepartmentListPage
    title="Sales Reports"
    subtitle="Monthly sales review and pipeline insights."
    endpoint="/employee/sales/reports"
    columns={[
      { key: "title", label: "Report" },
      { key: "period", label: "Period" },
      { key: "owner", label: "Owner" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "title", label: "Report Title", required: true },
      { key: "period", label: "Period" },
      { key: "owner", label: "Owner" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["reports:sales:view"]}
    approvePerms={["reports:sales:view"]}
    statusActions={[
      { label: "Publish", value: "published", variant: "success" },
      { label: "Draft", value: "draft", variant: "outline-secondary" },
    ]}
  />
);

export const PlantProductionPlans = () => (
  <DepartmentListPage
    title="Production Plans"
    subtitle="Daily production plans with targets and shifts."
    endpoint="/employee/plant/production-plans"
    columns={[
      { key: "planDate", label: "Date", render: (r) => fmtDate(r.planDate) },
      { key: "shift", label: "Shift" },
      { key: "line", label: "Line" },
      { key: "targetQty", label: "Target Qty" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "planDate", label: "Plan Date", type: "date", required: true },
      { key: "shift", label: "Shift" },
      { key: "line", label: "Line" },
      { key: "targetQty", label: "Target Qty" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["production:plan"]}
    approvePerms={["production:plan"]}
    statusActions={[
      { label: "Start", value: "in-progress", variant: "success" },
      { label: "Complete", value: "completed", variant: "outline-primary" },
    ]}
  />
);

export const PlantProductionReports = () => (
  <DepartmentListPage
    title="Production Reports"
    subtitle="Shift-wise output and downtime reporting."
    endpoint="/employee/plant/production-reports"
    columns={[
      { key: "reportDate", label: "Date", render: (r) => fmtDate(r.reportDate) },
      { key: "shift", label: "Shift" },
      { key: "outputQty", label: "Output Qty" },
      { key: "downtime", label: "Downtime" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "reportDate", label: "Report Date", type: "date", required: true },
      { key: "shift", label: "Shift" },
      { key: "outputQty", label: "Output Qty" },
      { key: "downtime", label: "Downtime" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["production:report"]}
    approvePerms={["production:report"]}
    statusActions={[
      { label: "Submit", value: "submitted", variant: "success" },
      { label: "Approve", value: "approved", variant: "outline-primary" },
    ]}
  />
);

export const PlantMaintenanceLogs = () => (
  <DepartmentListPage
    title="Maintenance Logs"
    subtitle="Machine issues, priorities, and resolution tracking."
    endpoint="/employee/plant/maintenance-logs"
    columns={[
      { key: "machine", label: "Machine" },
      { key: "issue", label: "Issue" },
      { key: "priority", label: "Priority" },
      { key: "assignedTo", label: "Assigned To" },
      { key: "dueDate", label: "Due Date", render: (r) => fmtDate(r.dueDate) },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "machine", label: "Machine", required: true },
      { key: "issue", label: "Issue" },
      { key: "priority", label: "Priority" },
      { key: "assignedTo", label: "Assigned To" },
      { key: "dueDate", label: "Due Date", type: "date" },
    ]}
    createPerms={["maintenance:log"]}
    approvePerms={["maintenance:log"]}
    statusActions={[
      { label: "Resolve", value: "resolved", variant: "success" },
      { label: "In Progress", value: "in-progress", variant: "outline-secondary" },
    ]}
  />
);

export const PlantWastageLogs = () => (
  <DepartmentListPage
    title="Wastage Logs"
    subtitle="Material wastage reasons and approvals."
    endpoint="/employee/plant/wastage-logs"
    columns={[
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "material", label: "Material" },
      { key: "qty", label: "Quantity" },
      { key: "unit", label: "Unit" },
      { key: "department", label: "Department" },
      { key: "reason", label: "Reason" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "date", label: "Date", type: "date", required: true },
      { key: "material", label: "Material" },
      { key: "qty", label: "Quantity" },
      { key: "reason", label: "Reason" },
    ]}
    createPerms={["wastage:view"]}
    approvePerms={["wastage:view"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Logged", value: "logged", variant: "outline-secondary" },
    ]}
  />
);

export const PlantStockConsumption = () => (
  <DepartmentListPage
    title="Stock Consumption"
    subtitle="Raw material consumption by batch."
    endpoint="/employee/plant/stock-consumption"
    columns={[
      { key: "material", label: "Material" },
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "consumedQty", label: "Consumed Qty" },
      { key: "batchNo", label: "Batch" },
      { key: "remarks", label: "Remarks" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "material", label: "Material", required: true },
      { key: "date", label: "Date", type: "date" },
      { key: "consumedQty", label: "Consumed Qty" },
      { key: "batchNo", label: "Batch" },
      { key: "remarks", label: "Remarks" },
    ]}
    createPerms={["stock:consumption:view"]}
    approvePerms={["stock:consumption:view"]}
    statusActions={[
      { label: "Verify", value: "verified", variant: "success" },
      { label: "Logged", value: "logged", variant: "outline-secondary" },
    ]}
  />
);

export const PlantShiftPlans = () => (
  <DepartmentListPage
    title="Shift Plans"
    subtitle="Shift planning and manpower allocation."
    endpoint="/employee/plant/shift-plans"
    columns={[
      { key: "shiftDate", label: "Date", render: (r) => fmtDate(r.shiftDate) },
      { key: "shift", label: "Shift" },
      { key: "supervisors", label: "Supervisors" },
      { key: "workers", label: "Workers" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "shiftDate", label: "Shift Date", type: "date", required: true },
      { key: "shift", label: "Shift" },
      { key: "supervisors", label: "Supervisors" },
      { key: "workers", label: "Workers" },
    ]}
    createPerms={["plant:shift:manage"]}
    approvePerms={["plant:shift:manage"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Plan", value: "planned", variant: "outline-secondary" },
    ]}
  />
);

export const PlantOvertimeApprovals = () => (
  <DepartmentListPage
    title="Overtime Approvals"
    subtitle="Overtime requests and approvals."
    endpoint="/employee/plant/overtime-approvals"
    columns={[
      { key: "employeeName", label: "Employee" },
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "hours", label: "Hours" },
      { key: "reason", label: "Reason" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "employeeName", label: "Employee Name", required: true },
      { key: "date", label: "Date", type: "date" },
      { key: "hours", label: "Hours" },
      { key: "reason", label: "Reason" },
    ]}
    createPerms={["plant:overtime:approve"]}
    approvePerms={["plant:overtime:approve"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const PlantUtilization = () => (
  <DepartmentListPage
    title="Machine Utilization"
    subtitle="Utilization monitoring and review."
    endpoint="/employee/plant/utilization"
    columns={[
      { key: "machine", label: "Machine" },
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "utilizationPercent", label: "Utilization %" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "machine", label: "Machine", required: true },
      { key: "date", label: "Date", type: "date" },
      { key: "utilizationPercent", label: "Utilization %" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["plant:utilization:view"]}
    approvePerms={["plant:utilization:view"]}
    statusActions={[
      { label: "Review", value: "reviewed", variant: "success" },
      { label: "Logged", value: "logged", variant: "outline-secondary" },
    ]}
  />
);

export const PlantBreakdowns = () => (
  <DepartmentListPage
    title="Breakdown Reports"
    subtitle="Machine breakdowns and downtime analysis."
    endpoint="/employee/plant/breakdowns"
    columns={[
      { key: "machine", label: "Machine" },
      { key: "issue", label: "Issue" },
      { key: "downtimeMinutes", label: "Downtime (min)" },
      { key: "reportedBy", label: "Reported By" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "machine", label: "Machine", required: true },
      { key: "issue", label: "Issue" },
      { key: "downtimeMinutes", label: "Downtime Minutes" },
      { key: "reportedBy", label: "Reported By" },
    ]}
    createPerms={["plant:breakdown:view"]}
    approvePerms={["plant:breakdown:view"]}
    statusActions={[
      { label: "Resolve", value: "resolved", variant: "success" },
      { label: "Open", value: "open", variant: "outline-secondary" },
    ]}
  />
);

export const PlantMaintenanceSchedules = () => (
  <DepartmentListPage
    title="Maintenance Schedules"
    subtitle="Preventive maintenance approvals."
    endpoint="/employee/plant/maintenance-schedules"
    columns={[
      { key: "machine", label: "Machine" },
      { key: "scheduleDate", label: "Date", render: (r) => fmtDate(r.scheduleDate) },
      { key: "type", label: "Type" },
      { key: "assignedTo", label: "Assigned To" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "machine", label: "Machine", required: true },
      { key: "scheduleDate", label: "Schedule Date", type: "date" },
      { key: "type", label: "Type" },
      { key: "assignedTo", label: "Assigned To" },
    ]}
    createPerms={["plant:maintenance:approve"]}
    approvePerms={["plant:maintenance:approve"]}
    statusActions={[
      { label: "Complete", value: "completed", variant: "success" },
      { label: "Schedule", value: "scheduled", variant: "outline-secondary" },
    ]}
  />
);

export const PlantQualityReviews = () => (
  <DepartmentListPage
    title="Quality Reviews"
    subtitle="QC reports and corrective actions."
    endpoint="/employee/plant/quality-reviews"
    columns={[
      { key: "batchNo", label: "Batch" },
      { key: "product", label: "Product" },
      { key: "reportSummary", label: "Summary" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "batchNo", label: "Batch No", required: true },
      { key: "product", label: "Product" },
      { key: "reportSummary", label: "Summary" },
    ]}
    createPerms={["quality:check:create"]}
    approvePerms={["quality:approve"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Rework", value: "rework", variant: "outline-danger" },
    ]}
  />
);

export const PlantWastageAnalysis = () => (
  <DepartmentListPage
    title="Wastage Analysis"
    subtitle="Scrap and wastage reports."
    endpoint="/employee/plant/wastage-analysis"
    columns={[
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "material", label: "Material" },
      { key: "qty", label: "Qty" },
      { key: "reason", label: "Reason" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "date", label: "Date", type: "date" },
      { key: "material", label: "Material" },
      { key: "qty", label: "Qty" },
      { key: "reason", label: "Reason" },
    ]}
    createPerms={["wastage:view"]}
    approvePerms={["wastage:view"]}
    statusActions={[
      { label: "Review", value: "reviewed", variant: "success" },
      { label: "Logged", value: "logged", variant: "outline-secondary" },
    ]}
  />
);

export const PlantMaterialMovement = () => (
  <DepartmentListPage
    title="Material Movement"
    subtitle="Inter-department movement approvals."
    endpoint="/employee/plant/material-movement"
    columns={[
      { key: "fromDept", label: "From" },
      { key: "toDept", label: "To" },
      { key: "item", label: "Item" },
      { key: "qty", label: "Qty" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "fromDept", label: "From Dept" },
      { key: "toDept", label: "To Dept" },
      { key: "item", label: "Item" },
      { key: "qty", label: "Qty" },
    ]}
    createPerms={["plant:material-movement:approve"]}
    approvePerms={["plant:material-movement:approve"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const PlantInventoryAlerts = () => (
  <DepartmentListPage
    title="Inventory Alerts"
    subtitle="Material shortage and alert handling."
    endpoint="/employee/plant/inventory-alerts"
    columns={[
      { key: "item", label: "Item" },
      { key: "currentStock", label: "Current Stock" },
      { key: "reorderLevel", label: "Reorder Level" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "item", label: "Item", required: true },
      { key: "currentStock", label: "Current Stock" },
      { key: "reorderLevel", label: "Reorder Level" },
    ]}
    createPerms={["plant:inventory:alert"]}
    approvePerms={["plant:inventory:alert"]}
    statusActions={[
      { label: "Resolve", value: "resolved", variant: "success" },
      { label: "Critical", value: "critical", variant: "outline-danger" },
    ]}
  />
);

export const PlantSafetyIncidents = () => (
  <DepartmentListPage
    title="Safety Incidents"
    subtitle="Accident and safety incident reviews."
    endpoint="/employee/plant/safety-incidents"
    columns={[
      { key: "incidentDate", label: "Date", render: (r) => fmtDate(r.incidentDate) },
      { key: "location", label: "Location" },
      { key: "severity", label: "Severity" },
      { key: "description", label: "Description" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "incidentDate", label: "Incident Date", type: "date" },
      { key: "location", label: "Location" },
      { key: "severity", label: "Severity" },
      { key: "description", label: "Description" },
    ]}
    createPerms={["plant:safety:manage"]}
    approvePerms={["plant:safety:manage"]}
    statusActions={[
      { label: "Close", value: "closed", variant: "success" },
      { label: "Open", value: "open", variant: "outline-secondary" },
    ]}
  />
);

export const PlantComplianceAudits = () => (
  <DepartmentListPage
    title="Compliance Audits"
    subtitle="Factory rules and audit compliance."
    endpoint="/employee/plant/compliance-audits"
    columns={[
      { key: "auditDate", label: "Audit Date", render: (r) => fmtDate(r.auditDate) },
      { key: "auditor", label: "Auditor" },
      { key: "score", label: "Score" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "auditDate", label: "Audit Date", type: "date" },
      { key: "auditor", label: "Auditor" },
      { key: "score", label: "Score" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["plant:compliance:manage"]}
    approvePerms={["plant:compliance:manage"]}
    statusActions={[
      { label: "Complete", value: "completed", variant: "success" },
      { label: "Pending", value: "pending", variant: "outline-secondary" },
    ]}
  />
);

export const PlantCoordinationLogs = () => (
  <DepartmentListPage
    title="Coordination Logs"
    subtitle="Inter-department coordination notes."
    endpoint="/employee/plant/coordination-logs"
    columns={[
      { key: "topic", label: "Topic" },
      { key: "department", label: "Department" },
      { key: "owner", label: "Owner" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "topic", label: "Topic", required: true },
      { key: "department", label: "Department" },
      { key: "owner", label: "Owner" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["plant:coordination:manage"]}
    approvePerms={["plant:coordination:manage"]}
    statusActions={[
      { label: "Close", value: "closed", variant: "success" },
      { label: "Open", value: "open", variant: "outline-secondary" },
    ]}
  />
);

export const PlantPerformanceReports = () => (
  <DepartmentListPage
    title="Performance Reports"
    subtitle="Target vs actual production and delays."
    endpoint="/employee/plant/performance-reports"
    columns={[
      { key: "period", label: "Period" },
      { key: "targetQty", label: "Target" },
      { key: "actualQty", label: "Actual" },
      { key: "delayNotes", label: "Delay Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "period", label: "Period", required: true },
      { key: "targetQty", label: "Target Qty" },
      { key: "actualQty", label: "Actual Qty" },
      { key: "delayNotes", label: "Delay Notes" },
    ]}
    createPerms={["plant:reports:manage"]}
    approvePerms={["plant:reports:manage"]}
    statusActions={[
      { label: "Publish", value: "published", variant: "success" },
      { label: "Draft", value: "draft", variant: "outline-secondary" },
    ]}
  />
);

export const PurchaseRequests = () => (
  <DepartmentListPage
    title="Purchase Requests"
    subtitle="Material requests raised by departments."
    endpoint="/employee/purchase/requests"
    columns={[
      { key: "requestNo", label: "Request #" },
      { key: "item", label: "Item" },
      { key: "qty", label: "Quantity" },
      { key: "neededBy", label: "Needed By", render: (r) => fmtDate(r.neededBy) },
      { key: "requestedBy", label: "Requested By" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "requestNo", label: "Request # (blank = auto)" },
      { key: "item", label: "Item", required: true },
      { key: "qty", label: "Quantity", type: "number", required: true },
      { key: "unit", label: "Unit", type: "select", options: ["kg", "ton", "bag", "piece"].map((v) => ({ value: v, label: v })) },
      { key: "neededBy", label: "Needed By", type: "date", required: true },
      { key: "requestedBy", label: "Requested By" },
      { key: "department", label: "Department" },
      { key: "purpose", label: "Purpose" },
      { key: "specifications", label: "Specifications" },
    ]}
    createPerms={["purchase:request:create"]}
    approvePerms={["purchase:request:create"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const PurchaseOrders = () => (
  <DepartmentListPage
    title="Purchase Orders"
    subtitle="POs issued to vendors and delivery dates."
    endpoint="/employee/purchase/orders"
    columns={[
      { key: "poNumber", label: "PO #" },
      { key: "product", label: "Product" },
      { key: "vendor", label: "Vendor" },
      { key: "qty", label: "Qty" },
      { key: "rate", label: "Rate" },
      { key: "amount", label: "Amount" },
      { key: "deliveryDate", label: "Delivery Date", render: (r) => fmtDate(r.deliveryDate) },
      { key: "buyer", label: "Buyer" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "quoteId", label: "Finalized Quotation", type: "select", optionsEndpoint: "/employee/purchase/workflow-options", optionsKey: "quotes", required: true },
      { key: "poNumber", label: "PO # (blank = auto)" },
      { key: "deliveryDate", label: "Delivery Date", type: "date", required: true },
      { key: "buyer", label: "Buyer" },
      { key: "terms", label: "Additional Terms" },
    ]}
    createPerms={["purchase:order:create"]}
    approvePerms={["purchase:order:create"]}
    statusActions={[
      { label: "Approve & Assign", value: "approved", variant: "success" },
      { label: "Close", value: "closed", variant: "outline-success" },
      { label: "Cancel", value: "cancelled", variant: "outline-danger" },
    ]}
  />
);

export const PurchaseVendorMaster = () => (
  <DepartmentListPage
    title="Vendor Master"
    subtitle="Preferred suppliers and ratings."
    endpoint="/employee/purchase/vendor-master"
    columns={[
      { key: "vendorName", label: "Vendor" },
      { key: "category", label: "Category" },
      { key: "contact", label: "Contact" },
      { key: "rating", label: "Rating" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "vendorName", label: "Vendor Name", required: true },
      { key: "category", label: "Category" },
      { key: "contact", label: "Contact" },
      { key: "rating", label: "Rating" },
    ]}
    createPerms={["vendor-master:edit"]}
    approvePerms={["vendor-master:edit"]}
    statusActions={[
      { label: "Activate", value: "active", variant: "success" },
      { label: "Deactivate", value: "inactive", variant: "outline-danger" },
    ]}
  />
);

export const PurchaseRateComparisons = () => (
  <DepartmentListPage
    title="Rate Comparisons"
    subtitle="Vendor quotes and comparative rates."
    endpoint="/employee/purchase/rate-comparisons"
    columns={[
      { key: "item", label: "Item" },
      { key: "requestNo", label: "Request #" },
      { key: "vendor", label: "Vendor" },
      { key: "rate", label: "Rate" },
      { key: "validTill", label: "Valid Till", render: (r) => fmtDate(r.validTill) },
      { key: "remarks", label: "Remarks" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "requestId", label: "Approved Request", type: "select", optionsEndpoint: "/employee/purchase/workflow-options", optionsKey: "requests", required: true },
      { key: "supplierUser", label: "Approved Supplier", type: "select", optionsEndpoint: "/employee/purchase/workflow-options", optionsKey: "suppliers", required: true },
      { key: "vendor", label: "Supplier Name", required: true },
      { key: "rate", label: "Rate", type: "number", required: true },
      { key: "taxPercent", label: "Tax %", type: "number" },
      { key: "freightAmount", label: "Freight Amount", type: "number" },
      { key: "validTill", label: "Valid Till", type: "date" },
      { key: "deliveryDays", label: "Delivery Days", type: "number" },
      { key: "paymentTerms", label: "Payment Terms" },
      { key: "remarks", label: "Remarks" },
    ]}
    createPerms={["purchase:rate-comparison:view"]}
    approvePerms={["purchase:rate-comparison:view"]}
    statusActions={[
      { label: "Finalize", value: "final", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const PurchaseMaterialAvailability = () => (
  <DepartmentListPage
    title="Material Availability"
    subtitle="Current stock availability and reorder levels."
    endpoint="/employee/purchase/material-availability"
    columns={[
      { key: "item", label: "Item" },
      { key: "currentStock", label: "Current Stock" },
      { key: "reorderLevel", label: "Reorder Level" },
      { key: "availableAt", label: "Available At" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "item", label: "Item", required: true },
      { key: "currentStock", label: "Current Stock" },
      { key: "reorderLevel", label: "Reorder Level" },
      { key: "availableAt", label: "Available At" },
    ]}
    createPerms={["material:availability:view"]}
    approvePerms={["material:availability:view"]}
    statusActions={[
      { label: "Available", value: "available", variant: "success" },
      { label: "Low", value: "low", variant: "outline-warning" },
    ]}
  />
);

export const InventoryStockLedger = () => (
  <DepartmentListPage
    title="Stock Ledger"
    subtitle="Live stock view by SKU and location."
    endpoint="/employee/inventory/stock-ledger"
    columns={[
      { key: "item", label: "Item" },
      { key: "sku", label: "SKU" },
      { key: "location", label: "Location" },
      { key: "qty", label: "Qty" },
      { key: "uom", label: "UOM" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "item", label: "Item", required: true },
      { key: "sku", label: "SKU" },
      { key: "location", label: "Location" },
      { key: "qty", label: "Quantity" },
      { key: "uom", label: "UOM" },
    ]}
    createPerms={["inventory:stock:update"]}
    approvePerms={["inventory:stock:update"]}
    statusActions={[
      { label: "Ok", value: "ok", variant: "success" },
      { label: "Critical", value: "critical", variant: "outline-danger" },
    ]}
  />
);

export const InventoryGRN = () => (
  <DepartmentListPage
    title="GRN Register"
    subtitle="Goods received notes and inspection status."
    endpoint="/employee/inventory/grn"
    columns={[
      { key: "grnNumber", label: "GRN #" },
      { key: "vendor", label: "Vendor" },
      { key: "receivedDate", label: "Received", render: (r) => fmtDate(r.receivedDate) },
      { key: "qty", label: "Qty" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "grnNumber", label: "GRN #", required: true },
      { key: "vendor", label: "Vendor" },
      { key: "receivedDate", label: "Received Date", type: "date" },
      { key: "qty", label: "Quantity" },
    ]}
    createPerms={["inventory:grn:create"]}
    approvePerms={["inventory:grn:create"]}
    statusActions={[
      { label: "Inspect", value: "inspected", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const InventoryIssueReturn = () => (
  <DepartmentListPage
    title="Issue & Return"
    subtitle="Material issues and returns to stores."
    endpoint="/employee/inventory/issue-return"
    columns={[
      { key: "issueNo", label: "Issue #" },
      { key: "item", label: "Item" },
      { key: "qty", label: "Qty" },
      { key: "issuedTo", label: "Issued To" },
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "issueNo", label: "Issue #", required: true },
      { key: "item", label: "Item" },
      { key: "qty", label: "Quantity" },
      { key: "issuedTo", label: "Issued To" },
      { key: "date", label: "Date", type: "date" },
    ]}
    createPerms={["inventory:issue:return"]}
    approvePerms={["inventory:issue:return"]}
    statusActions={[
      { label: "Returned", value: "returned", variant: "success" },
      { label: "Issued", value: "issued", variant: "outline-secondary" },
    ]}
  />
);

export const InventoryAdjustments = () => (
  <DepartmentListPage
    title="Stock Adjustments"
    subtitle="Stock correction requests and approvals."
    endpoint="/employee/inventory/adjustments"
    columns={[
      { key: "item", label: "Item" },
      { key: "adjustmentType", label: "Type" },
      { key: "qty", label: "Qty" },
      { key: "reason", label: "Reason" },
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "item", label: "Item", required: true },
      { key: "adjustmentType", label: "Adjustment Type" },
      { key: "qty", label: "Quantity" },
      { key: "reason", label: "Reason" },
      { key: "date", label: "Date", type: "date" },
    ]}
    createPerms={["inventory:stock:update"]}
    approvePerms={["inventory:stock:update"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Pending", value: "pending", variant: "outline-secondary" },
    ]}
  />
);

export const InventoryReports = () => (
  <DepartmentListPage
    title="Inventory Reports"
    subtitle="Stock aging and movement insights."
    endpoint="/employee/inventory/reports"
    columns={[
      { key: "title", label: "Report" },
      { key: "period", label: "Period" },
      { key: "owner", label: "Owner" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "title", label: "Report Title", required: true },
      { key: "period", label: "Period" },
      { key: "owner", label: "Owner" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["inventory:reports:view"]}
    approvePerms={["inventory:reports:view"]}
    statusActions={[
      { label: "Publish", value: "published", variant: "success" },
      { label: "Draft", value: "draft", variant: "outline-secondary" },
    ]}
  />
);

export const LogisticsShipments = () => (
  <DepartmentListPage
    title="Shipments"
    subtitle="Outbound shipments and delivery ETA."
    endpoint="/employee/logistics/shipments"
    columns={[
      { key: "shipmentNo", label: "Shipment #" },
      { key: "client", label: "Client" },
      { key: "destination", label: "Destination" },
      { key: "eta", label: "ETA", render: (r) => fmtDate(r.eta) },
      { key: "carrier", label: "Carrier" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "shipmentNo", label: "Shipment #", required: true },
      { key: "client", label: "Client" },
      { key: "destination", label: "Destination" },
      { key: "eta", label: "ETA", type: "date" },
      { key: "carrier", label: "Carrier" },
    ]}
    createPerms={["logistics:shipment:create"]}
    approvePerms={["logistics:dispatch:update"]}
    statusActions={[
      { label: "Dispatch", value: "in-transit", variant: "success" },
      { label: "Deliver", value: "delivered", variant: "outline-primary" },
    ]}
  />
);

export const LogisticsPlanning = () => (
  <DepartmentListPage
    title="Dispatch Planning"
    subtitle="Verify orders, set priority, and plan dispatch."
    endpoint="/employee/logistics/dispatch-requests"
    columns={[
      { key: "orderNo", label: "Order #" },
      { key: "customer", label: "Customer" },
      { key: "item", label: "Item" },
      { key: "quantity", label: "Qty" },
      { key: "address", label: "Address" },
      { key: "priority", label: "Priority" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "orderNo", label: "Order #", required: true },
      { key: "customer", label: "Customer" },
      { key: "item", label: "Item" },
      { key: "quantity", label: "Quantity" },
      { key: "address", label: "Address" },
      { key: "priority", label: "Priority (urgent/normal)" },
    ]}
    createPerms={["logistics:planning:manage"]}
    approvePerms={["logistics:planning:manage"]}
    statusActions={[
      { label: "Verify", value: "verified", variant: "success" },
      { label: "Plan", value: "planned", variant: "outline-primary" },
    ]}
  />
);

export const LogisticsPacking = () => (
  <DepartmentListPage
    title="Packing & Labels"
    subtitle="Packing list, boxes, pallets, and labeling."
    endpoint="/employee/logistics/packing-lists"
    columns={[
      { key: "orderNo", label: "Order #" },
      { key: "packingListNo", label: "Packing List" },
      { key: "boxes", label: "Boxes" },
      { key: "weight", label: "Weight" },
      { key: "pallets", label: "Pallets" },
      { key: "labelStatus", label: "Label" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "orderNo", label: "Order #", required: true },
      { key: "packingListNo", label: "Packing List No" },
      { key: "boxes", label: "Boxes" },
      { key: "weight", label: "Weight" },
      { key: "pallets", label: "Pallets" },
      { key: "labelStatus", label: "Label Status" },
    ]}
    createPerms={["logistics:packing:manage"]}
    approvePerms={["logistics:packing:manage"]}
    statusActions={[
      { label: "Packed", value: "packed", variant: "success" },
      { label: "Labeled", value: "labeled", variant: "outline-primary" },
    ]}
  />
);

export const LogisticsTransport = () => (
  <DepartmentListPage
    title="Vehicle & Transport"
    subtitle="Assign vehicle, driver, transporter and cost."
    endpoint="/employee/logistics/transport"
    columns={[
      { key: "dispatchNo", label: "Dispatch #" },
      { key: "vehicleNo", label: "Vehicle" },
      { key: "driverName", label: "Driver" },
      { key: "transporter", label: "Transporter" },
      { key: "transportType", label: "Type" },
      { key: "freightCost", label: "Cost" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "dispatchNo", label: "Dispatch #", required: true },
      { key: "vehicleNo", label: "Vehicle No" },
      { key: "driverName", label: "Driver" },
      { key: "transporter", label: "Transporter" },
      { key: "transportType", label: "Company/Third-party" },
      { key: "freightCost", label: "Freight Cost" },
    ]}
    createPerms={["logistics:transport:manage"]}
    approvePerms={["logistics:transport:manage"]}
    statusActions={[
      { label: "In-Transit", value: "in-transit", variant: "success" },
      { label: "Delivered", value: "delivered", variant: "outline-primary" },
    ]}
  />
);

export const LogisticsDispatchProcess = () => (
  <DepartmentListPage
    title="Dispatch Process"
    subtitle="Delivery challan, gate pass, and dispatch status."
    endpoint="/employee/logistics/dispatch-notes"
    columns={[
      { key: "dispatchNo", label: "Dispatch #" },
      { key: "challanNo", label: "Challan #" },
      { key: "gatePassNo", label: "Gate Pass" },
      { key: "dispatchDate", label: "Date", render: (r) => fmtDate(r.dispatchDate) },
      { key: "dispatchTime", label: "Time" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "dispatchNo", label: "Dispatch #", required: true },
      { key: "challanNo", label: "Challan #" },
      { key: "gatePassNo", label: "Gate Pass #" },
      { key: "dispatchDate", label: "Dispatch Date", type: "date" },
      { key: "dispatchTime", label: "Dispatch Time" },
    ]}
    createPerms={["logistics:dispatch:manage"]}
    approvePerms={["logistics:dispatch:manage"]}
    statusActions={[{ label: "Dispatch", value: "dispatched", variant: "success" }]}
  />
);

export const LogisticsTracking = () => (
  <DepartmentListPage
    title="Shipment Tracking"
    subtitle="LR/AWB updates, POD, and delivery status."
    endpoint="/employee/logistics/tracking"
    columns={[
      { key: "dispatchNo", label: "Dispatch #" },
      { key: "lrNo", label: "LR No" },
      { key: "awbNo", label: "AWB No" },
      { key: "podLink", label: "POD" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "dispatchNo", label: "Dispatch #", required: true },
      { key: "lrNo", label: "LR No" },
      { key: "awbNo", label: "AWB No" },
      { key: "podLink", label: "POD Link" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["logistics:tracking:manage"]}
    approvePerms={["logistics:tracking:manage"]}
    statusActions={[
      { label: "Delivered", value: "delivered", variant: "success" },
      { label: "Delayed", value: "delayed", variant: "outline-danger" },
    ]}
  />
);

export const LogisticsReturns = () => (
  <DepartmentListPage
    title="Returns / Reverse Logistics"
    subtitle="Damaged, rejected goods and return pickups."
    endpoint="/employee/logistics/returns"
    columns={[
      { key: "returnNo", label: "Return #" },
      { key: "orderNo", label: "Order #" },
      { key: "reason", label: "Reason" },
      { key: "pickupDate", label: "Pickup", render: (r) => fmtDate(r.pickupDate) },
      { key: "stockUpdated", label: "Stock Updated", render: (r) => (r.stockUpdated ? "Yes" : "No") },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "returnNo", label: "Return #", required: true },
      { key: "orderNo", label: "Order #" },
      { key: "reason", label: "Reason" },
      { key: "pickupDate", label: "Pickup Date", type: "date" },
      { key: "stockUpdated", label: "Stock Updated (true/false)" },
    ]}
    createPerms={["logistics:returns:manage"]}
    approvePerms={["logistics:returns:manage"]}
    statusActions={[
      { label: "Picked", value: "picked", variant: "success" },
      { label: "Received", value: "received", variant: "outline-primary" },
    ]}
  />
);

export const LogisticsReports = () => (
  <DepartmentListPage
    title="Logistics Reports"
    subtitle="Pending vs delivered and transport cost summary."
    endpoint="/employee/logistics/reports"
    columns={[
      { key: "title", label: "Report" },
      { key: "period", label: "Period" },
      { key: "pendingCount", label: "Pending" },
      { key: "deliveredCount", label: "Delivered" },
      { key: "transportCost", label: "Transport Cost" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "title", label: "Report Title", required: true },
      { key: "period", label: "Period" },
      { key: "pendingCount", label: "Pending Count" },
      { key: "deliveredCount", label: "Delivered Count" },
      { key: "transportCost", label: "Transport Cost" },
    ]}
    createPerms={["logistics:reports:manage"]}
    approvePerms={["logistics:reports:manage"]}
    statusActions={[
      { label: "Publish", value: "published", variant: "success" },
      { label: "Draft", value: "draft", variant: "outline-secondary" },
    ]}
  />
);

export const LogisticsGatepass = () => (
  <DepartmentListPage
    title="Gate Pass"
    subtitle="Vehicle entry/exit gate passes."
    endpoint="/employee/logistics/gatepass"
    columns={[
      { key: "gatePassNo", label: "Gate Pass #" },
      { key: "vehicle", label: "Vehicle" },
      { key: "driver", label: "Driver" },
      { key: "purpose", label: "Purpose" },
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "gatePassNo", label: "Gate Pass #", required: true },
      { key: "vehicle", label: "Vehicle" },
      { key: "driver", label: "Driver" },
      { key: "purpose", label: "Purpose" },
      { key: "date", label: "Date", type: "date" },
    ]}
    createPerms={["logistics:gatepass:create"]}
    approvePerms={["logistics:gatepass:create"]}
    statusActions={[
      { label: "Close", value: "closed", variant: "success" },
      { label: "Issued", value: "issued", variant: "outline-secondary" },
    ]}
  />
);

const formatQty = (row = {}) => `${orDash(row.quantity)} ${row.unit || ""}`.trim();
const qualityStatus = (row = {}) => row.qualityInspection?.status || "pending";
const PADDY_QUALITY_CHECKS = [
  { label: "IR64 PADDY - MOISTURE", expectedPercent: "14" },
  { label: "FOREIGN MATTER", expectedPercent: "1" },
  { label: "DAMAGED, DISCOLOURED", expectedPercent: "3" },
  { label: "RECOVERY RICE", expectedPercent: "65" },
  { label: "IMMATURE, SHRUNKEN AND SHRIVELLED", expectedPercent: "3" },
  { label: "ADMIXTURE OF LOWES CLASS", expectedPercent: "5" },
];

const isPaddyMaterial = (row = {}) => {
  const formType = String(row.inspectionReport?.formType || "").toLowerCase();
  const material = `${row.materialName || ""} ${row.productName || ""}`.toLowerCase();
  return formType === "paddy" || /\bpaddy\b/.test(material);
};

const qualityChecksFromDispatch = (row = {}) => {
  const saved = Array.isArray(row.qualityInspection?.qualityChecks) ? row.qualityInspection.qualityChecks : [];
  if (saved.length) {
    return saved.map((item) => ({
      label: item.label || "",
      expectedPercent: item.expectedPercent ?? "",
      actualValue: item.actualValue || "",
      result: item.result || "pending",
      remarks: item.remarks || "",
    }));
  }

  const dispatchChecks = Array.isArray(row.inspectionReport?.qualityChecks) ? row.inspectionReport.qualityChecks : [];
  if (dispatchChecks.length) {
    return dispatchChecks.map((item) => ({
      label: item.label || "",
      expectedPercent: item.percent ?? "",
      actualValue: item.unloadingReport || "",
      result: "pending",
      remarks: "",
    }));
  }

  if (isPaddyMaterial(row)) {
    return PADDY_QUALITY_CHECKS.map((item) => ({
      label: item.label,
      expectedPercent: item.expectedPercent,
      actualValue: "",
      result: "pending",
      remarks: "",
    }));
  }

  return [];
};

export const QAChecks = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    status: "pending",
    inspectorName: "",
    remarks: "",
    vehicleNo: "",
    receivedWeight: "",
    shortWeight: "",
    finalWeight: "",
    acceptedQty: "",
    rejectedQty: "",
    ratePerUnit: "",
    deductionAmount: "",
    proposedAmount: "",
    qualityChecks: [],
  });

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setErr("");
      const params = status === "all" ? {} : { status };
      const res = await API.get("/employee/qa/supplier-dispatches", { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load supplier material for quality check");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const summary = useMemo(() => ({
    total: rows.length,
    pending: rows.filter((row) => qualityStatus(row) === "pending").length,
    passed: rows.filter((row) => qualityStatus(row) === "passed").length,
    failed: rows.filter((row) => qualityStatus(row) === "failed").length,
  }), [rows]);

  const openQualityFormat = (row) => {
    setSelected(row);
    setForm({
      status: row.qualityInspection?.status || "pending",
      inspectorName: row.qualityInspection?.inspectorName || "",
      remarks: row.qualityInspection?.remarks || "",
      vehicleNo: row.qualityInspection?.vehicleNo || row.inspectionReport?.vehicleNo || row.vehicle?.number || "",
      receivedWeight: row.qualityInspection?.receivedWeight ?? row.inspectionReport?.weight ?? row.loading?.grossWeight ?? "",
      shortWeight: row.qualityInspection?.shortWeight ?? row.inspectionReport?.shortWeight ?? "",
      finalWeight: row.qualityInspection?.finalWeight ?? row.inspectionReport?.finalWeight ?? row.loading?.netWeight ?? "",
      acceptedQty: row.qualityInspection?.acceptedQty ?? row.quantity ?? "",
      rejectedQty: row.qualityInspection?.rejectedQty ?? "",
      ratePerUnit: row.qualityInspection?.ratePerUnit ?? "",
      deductionAmount: row.qualityInspection?.deductionAmount ?? "",
      proposedAmount: row.qualityInspection?.proposedAmount ?? "",
      qualityChecks: qualityChecksFromDispatch(row),
    });
    setMessage("");
    setErr("");
  };

  const setCheckField = (index, key, value) => {
    setForm((prev) => {
      const qualityChecks = [...prev.qualityChecks];
      qualityChecks[index] = { ...qualityChecks[index], [key]: value };
      return { ...prev, qualityChecks };
    });
  };

  const saveQuality = async (e) => {
    e.preventDefault();
    if (!selected?._id) return;
    try {
      setSaving(true);
      setErr("");
      await API.patch(`/employee/qa/supplier-dispatches/${selected._id}/quality`, form);
      setSelected(null);
      setMessage("Quality inspection format saved.");
      fetchRows();
    } catch (error) {
      setErr(error?.response?.data?.message || "Quality inspection save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container className="mt-4 accounts-page">
      <div className="accounts-header">
        <div>
          <div className="accounts-title-row">
            <h4 className="mb-0">Quality Checks</h4>
            <span className="accounts-count">{rows.length} dispatches</span>
          </div>
          <p className="accounts-subtitle">
            Supplier ne jo maal bheja hai, uska receiving quality format yahan fill kijiye.
          </p>
        </div>
        <div className="accounts-actions">
          <Form.Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 150 }}>
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
            <option value="recheck">Recheck</option>
          </Form.Select>
          <Button variant="outline-secondary" size="sm" onClick={fetchRows}>Refresh</Button>
        </div>
      </div>

      <section className="accounts-module-summary mb-3">
        <article><span>Total Material</span><strong>{summary.total}</strong></article>
        <article><span>Pending Check</span><strong>{summary.pending}</strong></article>
        <article><span>Passed</span><strong>{summary.passed}</strong></article>
        <article><span>Failed</span><strong>{summary.failed}</strong></article>
      </section>

      {err && <Alert variant="danger">{err}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
      ) : (
        <div className="accounts-table-card">
          <Table hover responsive className="accounts-table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Material</th>
                <th>Qty</th>
                <th>Vehicle</th>
                <th>PO / Batch</th>
                <th>Dispatch Status</th>
                <th>Quality Status</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="8" className="text-center">No supplier material pending for quality.</td></tr>
              ) : rows.map((row) => (
                <tr key={row._id}>
                  <td>
                    {row.supplierUser?._id ? (
                      <Link to={`/employee/suppliers/${row.supplierUser._id}`} className="fw-bold text-decoration-none">
                        {row.companyName || row.supplierName || "—"}
                      </Link>
                    ) : (
                      <strong>{row.companyName || row.supplierName || "—"}</strong>
                    )}
                    <div className="text-muted small">{orDash(row.contactMobile)}</div>
                  </td>
                  <td>{orDash(row.materialName || row.productName)}</td>
                  <td>{formatQty(row)}</td>
                  <td>{orDash(row.vehicle?.number)}</td>
                  <td>{orDash(row.purchaseOrderNo)}<div className="text-muted small">{orDash(row.batchNumber)}</div></td>
                  <td>{statusBadge(row.approvalStatus)}</td>
                  <td>{statusBadge(qualityStatus(row))}</td>
                  <td className="text-end">
                    <Button size="sm" variant="primary" onClick={() => openQualityFormat(row)}>
                      Quality Format
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={!!selected} onHide={() => setSelected(null)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Supplier Material Quality Format</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <Form onSubmit={saveQuality}>
              <div className="accounts-module-summary mb-3">
                <article><span>Supplier</span><strong>{selected.companyName || selected.supplierName || "—"}</strong></article>
                <article><span>Material</span><strong>{selected.materialName || selected.productName || "—"}</strong></article>
                <article><span>Vehicle</span><strong>{selected.vehicle?.number || "—"}</strong></article>
                <article><span>Quantity</span><strong>{formatQty(selected)}</strong></article>
              </div>

              <div className="table-responsive">
                <Table bordered size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>Quality Parameter</th>
                      <th>Allowed %</th>
	                      <th>Actual Checked Value</th>
                      <th>Result</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.qualityChecks.length === 0 ? (
                      <tr><td colSpan="5" className="text-center text-muted">No predefined quality parameters.</td></tr>
                    ) : form.qualityChecks.map((item, index) => (
                      <tr key={`${item.label}-${index}`}>
                        <td>{item.label || "—"}</td>
                        <td>{item.expectedPercent !== "" ? `${item.expectedPercent}%` : "—"}</td>
                        <td>
                          <Form.Control
                            value={item.actualValue}
                            onChange={(e) => setCheckField(index, "actualValue", e.target.value)}
                            placeholder="Checked value"
                          />
                        </td>
                        <td>
                          <Form.Select value={item.result} onChange={(e) => setCheckField(index, "result", e.target.value)}>
                            <option value="pending">Pending</option>
                            <option value="passed">Pass</option>
                            <option value="failed">Fail</option>
                            <option value="recheck">Recheck</option>
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Control
                            value={item.remarks}
                            onChange={(e) => setCheckField(index, "remarks", e.target.value)}
                            placeholder="Remarks"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="row g-3 mt-2">
                <Form.Group className="col-md-3">
                  <Form.Label>Vehicle No.</Form.Label>
                  <Form.Control value={form.vehicleNo} onChange={(e) => setForm({ ...form, vehicleNo: e.target.value })} />
                </Form.Group>
                <Form.Group className="col-md-3">
                  <Form.Label>Received Weight</Form.Label>
                  <Form.Control type="number" value={form.receivedWeight} onChange={(e) => setForm({ ...form, receivedWeight: e.target.value })} />
                </Form.Group>
                <Form.Group className="col-md-3">
                  <Form.Label>Short Weight</Form.Label>
                  <Form.Control type="number" value={form.shortWeight} onChange={(e) => setForm({ ...form, shortWeight: e.target.value })} />
                </Form.Group>
                <Form.Group className="col-md-3">
                  <Form.Label>Final Weight</Form.Label>
                  <Form.Control type="number" value={form.finalWeight} onChange={(e) => setForm({ ...form, finalWeight: e.target.value })} />
                </Form.Group>
                <Form.Group className="col-md-4">
                  <Form.Label>Inspector Name</Form.Label>
                  <Form.Control value={form.inspectorName} onChange={(e) => setForm({ ...form, inspectorName: e.target.value })} />
                </Form.Group>
                <Form.Group className="col-md-4">
                  <Form.Label>Final Result</Form.Label>
                  <Form.Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <option value="pending">Pending</option>
                    <option value="passed">Pass</option>
                    <option value="failed">Fail</option>
                    <option value="recheck">Recheck</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="col-md-4">
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
                </Form.Group>
                <Form.Group className="col-md-3">
                  <Form.Label>Accepted Qty</Form.Label>
                  <Form.Control type="number" value={form.acceptedQty} onChange={(e) => setForm({ ...form, acceptedQty: e.target.value })} />
                </Form.Group>
                <Form.Group className="col-md-3">
                  <Form.Label>Rejected Qty</Form.Label>
                  <Form.Control type="number" value={form.rejectedQty} onChange={(e) => setForm({ ...form, rejectedQty: e.target.value })} />
                </Form.Group>
                <Form.Group className="col-md-3">
                  <Form.Label>Rate / Unit</Form.Label>
                  <Form.Control type="number" value={form.ratePerUnit} onChange={(e) => {
                    const ratePerUnit = e.target.value;
                    const amount = Number(form.acceptedQty || 0) * Number(ratePerUnit || 0) - Number(form.deductionAmount || 0);
                    setForm({ ...form, ratePerUnit, proposedAmount: Number.isFinite(amount) ? Math.max(0, amount) : form.proposedAmount });
                  }} />
                </Form.Group>
                <Form.Group className="col-md-3">
                  <Form.Label>Deduction Amount</Form.Label>
                  <Form.Control type="number" value={form.deductionAmount} onChange={(e) => {
                    const deductionAmount = e.target.value;
                    const amount = Number(form.acceptedQty || 0) * Number(form.ratePerUnit || 0) - Number(deductionAmount || 0);
                    setForm({ ...form, deductionAmount, proposedAmount: Number.isFinite(amount) ? Math.max(0, amount) : form.proposedAmount });
                  }} />
                </Form.Group>
                <Form.Group className="col-md-4">
                  <Form.Label>Final Payable Amount</Form.Label>
                  <Form.Control type="number" value={form.proposedAmount} onChange={(e) => setForm({ ...form, proposedAmount: e.target.value })} />
                </Form.Group>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button variant="outline-secondary" onClick={() => setSelected(null)}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={saving}>{saving ? "Saving..." : "Save Quality Check"}</Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export const QARejections = () => (
  <DepartmentListPage
    title="Rejection Notes"
    subtitle="Rejected batches and root-cause summaries."
    endpoint="/employee/qa/rejections"
    columns={[
      { key: "batchNo", label: "Batch" },
      { key: "product", label: "Product" },
      { key: "qty", label: "Qty" },
      { key: "reason", label: "Reason" },
      { key: "reportedBy", label: "Reported By" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "batchNo", label: "Batch No", required: true },
      { key: "product", label: "Product" },
      { key: "qty", label: "Quantity" },
      { key: "reason", label: "Reason" },
      { key: "reportedBy", label: "Reported By" },
    ]}
    createPerms={["quality:reject:create"]}
    approvePerms={["quality:approve"]}
    statusActions={[
      { label: "Close", value: "closed", variant: "success" },
      { label: "Open", value: "open", variant: "outline-secondary" },
    ]}
  />
);

export const QAApprovals = () => (
  <DepartmentListPage
    title="QA Approvals"
    subtitle="Final approval or rejection decisions."
    endpoint="/employee/qa/approvals"
    columns={[
      { key: "batchNo", label: "Batch" },
      { key: "product", label: "Product" },
      { key: "decision", label: "Decision" },
      { key: "approvedBy", label: "Approved By" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "batchNo", label: "Batch No", required: true },
      { key: "product", label: "Product" },
      { key: "decision", label: "Decision" },
      { key: "approvedBy", label: "Approved By" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["quality:approve"]}
    approvePerms={["quality:approve"]}
    statusActions={[
      { label: "Approve", value: "approved", variant: "success" },
      { label: "Reject", value: "rejected", variant: "outline-danger" },
    ]}
  />
);

export const QAReports = () => (
  <DepartmentListPage
    title="QA Reports"
    subtitle="Quality metrics and compliance summaries."
    endpoint="/employee/qa/reports"
    columns={[
      { key: "title", label: "Report" },
      { key: "period", label: "Period" },
      { key: "owner", label: "Owner" },
      { key: "notes", label: "Notes" },
      { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    ]}
    createFields={[
      { key: "title", label: "Report Title", required: true },
      { key: "period", label: "Period" },
      { key: "owner", label: "Owner" },
      { key: "notes", label: "Notes" },
    ]}
    createPerms={["quality:reports:view"]}
    approvePerms={["quality:reports:view"]}
    statusActions={[
      { label: "Publish", value: "published", variant: "success" },
      { label: "Draft", value: "draft", variant: "outline-secondary" },
    ]}
  />
);

export const DepartmentPages = {
  HrEmployees,
  HrLeaveRequests,
  HrAttendanceApprovals,
  HrPerformanceReviews,
  HrReports,
  HrOnboarding,
  HrOffboarding,
  HrRecruitment,
  HrPolicies,
  HrTraining,
  HrGrievance,
  HrVacancies,
  HrInterviews,
  HrOfferLetters,
  HrHolidays,
  HrAttendanceRules,
  HrSalaryStructures,
  HrPayslips,
  HrResignations,
  HrExitInterviews,
  HrLetters,
  HrDocuments,
  HrPayrollRuns,
  HrDeductionRules,
  HrCandidateProfiles,
  HrAppraisals,
  SalesLeads,
  SalesMetaLeads,
  SalesDeals,
  SalesTargets,
  SalesPricingApprovals,
  SalesReports,
  PlantProductionPlans,
  PlantProductionReports,
  PlantMaintenanceLogs,
  PlantWastageLogs,
  PlantStockConsumption,
  PlantShiftPlans,
  PlantOvertimeApprovals,
  PlantUtilization,
  PlantBreakdowns,
  PlantMaintenanceSchedules,
  PlantQualityReviews,
  PlantWastageAnalysis,
  PlantMaterialMovement,
  PlantInventoryAlerts,
  PlantSafetyIncidents,
  PlantComplianceAudits,
  PlantCoordinationLogs,
  PlantPerformanceReports,
  PurchaseRequests,
  PurchaseOrders,
  PurchaseVendorMaster,
  PurchaseRateComparisons,
  PurchaseMaterialAvailability,
  InventoryStockLedger,
  InventoryGRN,
  InventoryIssueReturn,
  InventoryAdjustments,
  InventoryReports,
  LogisticsShipments,
  LogisticsTracking,
  LogisticsPlanning,
  LogisticsPacking,
  LogisticsTransport,
  LogisticsDispatchProcess,
  LogisticsReturns,
  LogisticsReports,
  LogisticsGatepass,
  QAChecks,
  QARejections,
  QAApprovals,
  QAReports,
};
